/**
 * AI CONTEXT: security - AWS Cognito authentication service with fallback
 * Journal: J-20250812-01
 * 
 * This service implements real authentication with:
 * - AWS Cognito user pool integration
 * - Secure session management
 * - Error handling and user feedback
 * - Age verification integration
 * - Mobile-first authentication flow
 * - Mock fallback for development
 * 
 * Architecture decisions:
 * - Use AWS Cognito for enterprise-grade security
 * - Implement proper error handling for user experience
 * - Support both email and phone authentication
 * - Integrate with existing AWS configuration
 * - Provide mock fallback for development
 * 
 * Future considerations:
 * - Add multi-factor authentication (MFA)
 * - Implement social login (Google, Facebook)
 * - Add biometric authentication for mobile
 * - Consider passwordless authentication
 */

import { 
  signUp, 
  signIn, 
  signOut, 
  getCurrentUser, 
  fetchAuthSession,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword
} from 'aws-amplify/auth';
import { toast } from 'react-hot-toast';
import awsConfig from './aws-config';
import { isApiConfigured, registerReferral, authorizedFetch } from './apiClient';
import { ensureDate } from '../utils/date';

// AI CONTEXT: security - Clear Amplify cached credentials
// Reasoning: Stale tokens/credentials in browser storage can cause login failures
// When incognito works but regular mode doesn't, it's due to cached auth data
// This function clears all Amplify-related storage to force fresh authentication
function clearAmplifyCache() {
  try {
    // Clear Amplify localStorage keys (tokens, credentials)
    const amplifyKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('CognitoIdentityServiceProvider.') ||
        key.startsWith('amplify-') ||
        key.startsWith('aws-amplify-') ||
        key.includes('Cognito')
      )) {
        amplifyKeys.push(key);
      }
    }
    amplifyKeys.forEach(key => localStorage.removeItem(key));
    
    // Clear IndexedDB (Amplify stores tokens here)
    if (typeof indexedDB !== 'undefined') {
      try {
        indexedDB.databases().then(databases => {
          databases.forEach(db => {
            if (db.name && (
              db.name.includes('amplify') || 
              db.name.includes('Cognito') ||
              db.name.includes('aws-amplify')
            )) {
              indexedDB.deleteDatabase(db.name);
            }
          });
        }).catch(() => {
          // IndexedDB cleanup errors are non-fatal
        });
      } catch (e) {
        // IndexedDB not available or error - ignore
      }
    }
    
    // Clear sessionStorage Amplify keys
    const sessionKeys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (
        key.startsWith('amplify-') ||
        key.startsWith('aws-amplify-') ||
        key.includes('Cognito')
      )) {
        sessionKeys.push(key);
      }
    }
    sessionKeys.forEach(key => sessionStorage.removeItem(key));
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔐 AuthService: Cleared Amplify cache', {
        localStorageKeys: amplifyKeys.length,
        sessionStorageKeys: sessionKeys.length
      });
    }
  } catch (error) {
    console.warn('🔐 AuthService: Error clearing Amplify cache:', error);
    // Non-fatal - continue even if cleanup fails
  }
}

// AI CONTEXT: security - Authentication error handling
// Reasoning: Provide clear, user-friendly error messages for better UX
// Constraints: Must handle various AWS Cognito error types
// Future: Consider implementing retry logic for network errors

const handleAuthError = (error) => {
  console.error('Authentication error:', error);
  
  // Map AWS Cognito errors to user-friendly messages
  const errorMessages = {
    'UserNotConfirmedException': 'Please check your email and confirm your account.',
    'NotAuthorizedException': 'Invalid email or password.',
    'UserNotFoundException': 'Account not found. Please check your email.',
    'UsernameExistsException': 'An account with this email already exists.',
    'InvalidPasswordException': 'Password must be at least 8 characters with uppercase, lowercase, number, and special character.',
    'CodeMismatchException': 'Invalid verification code. Please check your email.',
    'LimitExceededException': 'Too many attempts. Please try again later.',
    'ExpiredCodeException': 'Verification code has expired. Please request a new one.',
    'InvalidParameterException': 'Please check your input and try again.',
    'NetworkError': 'Network error. Please check your connection and try again.',
    'AuthUserPoolException': 'AWS Cognito not configured. Please set up AWS services.',
    'MockMode': 'Running in mock mode. Set up AWS Cognito for full functionality.'
  };

  const errorCode = error.code || error.name;
  const message = errorMessages[errorCode] || 'An error occurred. Please try again.';
  
  toast.error(message);
  return { success: false, error: errorCode, message };
};

// AI CONTEXT: security - Age verification integration
// Reasoning: Utah sweepstakes laws require 21+ verification for firearm giveaways
// Constraints: Must verify age before allowing certain features
// Future: Consider additional verification methods for enhanced security

const verifyAge = (birthDate) => {
  const today = new Date();
  const birth = ensureDate(birthDate);
  if (!birth) return 0;
  const age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    return age - 1;
  }
  return age;
};

// AI CONTEXT: development - Mock authentication for development
// Reasoning: Allow testing UI without AWS Cognito setup
// Constraints: Must simulate real authentication behavior
// Future: Remove when AWS Cognito is configured

const mockUsers = [
  {
    id: '1',
    email: 'demo@example.com',
    name: 'Demo User',
    phone: '+1234567890',
    birthDate: '1990-01-01',
    age: 34,
    joinDate: '2024-01-15',
    isSubscriber: true,
    isAdmin: false,
    entries: 15,
    purchases: []
  }
  // SECURITY: Removed mock admin user - use real AWS Cognito for admin access
  // Admin users should be created through AWS Cognito with proper security
];

class AuthService {
  // AI CONTEXT: security - User registration with age verification
  // Reasoning: Validate age and create user account with proper attributes
  // Constraints: Must verify age before allowing registration
  // Future: Consider implementing additional verification methods

  async register(userData) {
    try {
      const { email, password, name, phone, birthDate } = userData;
      
      // Verify age
      const age = verifyAge(birthDate);
      if (age < 21) {
        toast.error('You must be 21 or older to register');
        return { success: false, error: 'Age verification failed' };
      }

      if (!awsConfig.isConfigured) {
        // Mock registration
        const newUser = {
          id: Date.now().toString(),
          email,
          name,
          phone,
          birthDate,
          age,
          joinDate: new Date().toISOString().split('T')[0],
          isSubscriber: false,
          isAdmin: false,
          entries: 0,
          purchases: []
        };

        mockUsers.push(newUser);
        toast.success('Account created! (Mock mode - no email confirmation needed)');
        // Best-effort referral code allocation (if API configured)
        try {
          if (isApiConfigured()) {
            await registerReferral(email);
          }
        } catch (_) {}
        return { 
          success: true, 
          userId: newUser.id,
          requiresConfirmation: false,
          user: newUser
        };
      }

      // Format phone number for AWS Cognito (E.164 format)
      let formattedPhone = phone;
      if (phone && !phone.startsWith('+')) {
        // Remove all non-digit characters
        const digits = phone.replace(/\D/g, '');
        
        // If it's a US number (10 digits), add +1
        if (digits.length === 10) {
          formattedPhone = `+1${digits}`;
        } else if (digits.length === 11 && digits.startsWith('1')) {
          // Already has country code
          formattedPhone = `+${digits}`;
        } else {
          // For other formats, assume US and add +1
          formattedPhone = `+1${digits}`;
        }
      }

      // Real AWS Cognito registration
      // Prefer standard attributes where available. "birthdate" is a standard OIDC/Cognito attribute (YYYY-MM-DD).
      const attributes = {
        email,
        name,
        phone_number: formattedPhone,
        ...(birthDate ? { birthdate: birthDate } : {})
        // Note: We intentionally do NOT send custom attributes that may not be configured
        // in the user pool (e.g., custom:join_date, custom:age) to avoid schema errors.
      };

      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: attributes,
          autoSignIn: true
        }
      });

      toast.success('Account created! Please check your email to confirm your account.');
      // Generate referral code immediately (no auth required)
      try {
        if (isApiConfigured()) {
          await registerReferral(email);
        }
      } catch (_) {}
      return { 
        success: true, 
        userId: result.userId,
        requiresConfirmation: true 
      };
    } catch (error) {
      return handleAuthError(error);
    }
  }

  // AI CONTEXT: security - Account confirmation
  // Reasoning: Email verification required for security and legal compliance
  // Constraints: Must handle verification code properly
  // Future: Consider SMS verification as alternative

  async confirmAccount(email, code) {
    try {
      if (!awsConfig.isConfigured) {
        // Mock confirmation
        toast.success('Account confirmed! (Mock mode)');
        return { success: true };
      }

      await confirmSignUp({
        username: email,
        confirmationCode: code
      });

      toast.success('Account confirmed! You can now sign in.');
      return { success: true };
    } catch (error) {
      return handleAuthError(error);
    }
  }

  // AI CONTEXT: security - Confirm account and automatically sign in user
  // Reasoning: After email confirmation, automatically sign in user to enable profile data saving
  // Constraints: Must handle both confirmation and sign-in steps
  // Future: Consider implementing remember me functionality

  async confirmAccountAndSignIn(email, code, password) {
    try {
      if (!awsConfig.isConfigured) {
        // Mock confirmation and sign-in
        toast.success('Account confirmed and signed in! (Mock mode)');
        return { success: true, requiresSignIn: false };
      }

      // First, confirm the account
      await confirmSignUp({
        username: email,
        confirmationCode: code
      });

      // Then automatically sign in the user
      const signInResult = await signIn({
        username: email,
        password
      });

      if (signInResult.isSignedIn) {
        toast.success('Account confirmed and signed in successfully!');
        return { success: true, requiresSignIn: false };
      } else {
        toast.success('Account confirmed! Please sign in to continue.');
        return { success: true, requiresSignIn: true };
      }
    } catch (error) {
      return handleAuthError(error);
    }
  }

  // AI CONTEXT: security - Resend confirmation code
  // Reasoning: Users may not receive or lose confirmation emails
  // Constraints: Rate limiting applies
  // Future: Consider SMS as backup verification method

  async resendConfirmationCode(email) {
    try {
      if (!awsConfig.isConfigured) {
        toast.success('Confirmation code sent! (Mock mode)');
        return { success: true };
      }

      await resendSignUpCode({ username: email });
      toast.success('Confirmation code sent to your email.');
      return { success: true };
    } catch (error) {
      return handleAuthError(error);
    }
  }

  // AI CONTEXT: security - User login with proper authentication
  // Reasoning: Authenticate users securely with AWS Cognito
  // Constraints: Must handle both mock and real authentication
  // Future: Remove mock authentication for production

  async login(email, password) {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Login attempt - AWS configured:', awsConfig.isConfigured);
      }
      
      if (!awsConfig.isConfigured) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Using mock login mode');
        }
        // Mock login - SECURITY WARNING: Not secure for production
        const mockUser = mockUsers.find(user => user.email === email);
        
        if (!mockUser) {
          toast.error('Invalid email or password');
          return { success: false, error: 'INVALID_CREDENTIALS' };
        }

        // SECURITY: Mock login bypasses password verification
        // This is only for development - NOT SECURE for production
        toast.success('Logged in successfully! (Mock mode)');
        return { 
          success: true, 
          user: mockUser,
          requiresConfirmation: false
        };
      }

      // Real AWS Cognito login
      if (process.env.NODE_ENV !== 'production') {
        console.log('🔐 AuthService: Using real AWS Cognito login');
      }
      
      // AI CONTEXT: Clear stale Amplify cache before login attempts
      // When incognito works but regular mode fails, it's due to cached stale tokens
      // Proactively clear cache to prevent "Incorrect username or password" errors from stale data
      clearAmplifyCache();
      
      // AI CONTEXT: Use USER_PASSWORD_AUTH explicitly because app client was modified Oct 4
      // When ALLOW_ADMIN_USER_PASSWORD_AUTH was enabled, it may have changed default flow behavior
      // Explicitly specify USER_PASSWORD_AUTH to match what was working after Oct 4 fix
      const result = await signIn({
        username: email,
        password,
        options: {
          authFlowType: 'USER_PASSWORD_AUTH'
        }
      });
      console.log('🔐 AuthService: SignIn result:', {
        isSignedIn: result.isSignedIn,
        nextStep: result.nextStep,
        hasError: !!result.error,
        error: result.error
      });

      if (result.isSignedIn) {
        try {
          console.log('🔐 AuthService: SignIn successful, getting user details...');
          const userDetails = await this.getCurrentUserDetails();
          console.log('🔐 AuthService: User details retrieved:', userDetails);
          
          if (!userDetails) {
            console.error('🔐 AuthService: Failed to get user details after successful signIn');
            toast.error('Login successful but failed to load user profile');
            return { success: false, error: 'USER_DETAILS_FAILED' };
          }
          
          if (process.env.NODE_ENV !== 'production') {
            console.log('🔐 AuthService: Login successful, user details:', userDetails);
          }
          toast.success('Logged in successfully!');
          const loginResult = { 
            success: true, 
            user: userDetails,
            requiresConfirmation: false
          };
          return loginResult;
        } catch (error) {
          console.error('🔐 AuthService: Error in login after signIn:', error);
          return handleAuthError(error);
        }
      } else {
        console.log('🔐 AuthService: SignIn failed - isSignedIn is false');
        toast.error('Login failed. Please check your credentials.');
        return { success: false, error: 'LOGIN_FAILED' };
      }
    } catch (error) {
      // If login fails with NotAuthorizedException, clear cache and retry once
      // This handles cases where stale cached tokens cause false "Incorrect password" errors
      if (error.name === 'NotAuthorizedException' || 
          error.message?.includes('Incorrect username or password')) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('🔐 AuthService: Login failed - clearing cache and retrying...');
        }
        clearAmplifyCache();
        
        // Retry login once after clearing cache
        try {
          const retryResult = await signIn({
            username: email,
            password,
            options: {
              authFlowType: 'USER_PASSWORD_AUTH'
            }
          });
          
          if (retryResult.isSignedIn) {
            const userDetails = await this.getCurrentUserDetails();
            if (userDetails) {
              toast.success('Logged in successfully!');
              return { 
                success: true, 
                user: userDetails,
                requiresConfirmation: false
              };
            }
          }
        } catch (retryError) {
          // Retry also failed - proceed to normal error handling
          if (process.env.NODE_ENV !== 'production') {
            console.log('🔐 AuthService: Retry after cache clear also failed');
          }
        }
      }
      
      return handleAuthError(error);
    }
  }

  // AI CONTEXT: security - Secure logout
  // Reasoning: Properly clear all authentication data
  // Constraints: Must clear local storage and session data
  // Future: Consider implementing "logout from all devices"

  async logout() {
    try {
      if (!awsConfig.isConfigured) {
        // Mock logout
        localStorage.removeItem('tolbert_user');
        sessionStorage.clear();
        toast.success('Logged out successfully (Mock mode)');
        return { success: true };
      }

      await signOut();
      
      localStorage.removeItem('tolbert_user');
      sessionStorage.clear();
      
      toast.success('Logged out successfully');
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('tolbert_user');
      sessionStorage.clear();
      return { success: true };
    }
  }

  // AI CONTEXT: security - Get current user details
  // Reasoning: Retrieve user information for UI and business logic
  // Constraints: Must handle cases where user is not authenticated
  // Future: Consider caching user data for performance

  async getCurrentUserDetails() {
    try {
      if (!awsConfig.isConfigured) {
        // Return mock user if available
        const storedUser = localStorage.getItem('tolbert_user');
        return storedUser ? JSON.parse(storedUser) : null;
      }

      console.log('🔐 AuthService: Getting current user details...');
      const currentUser = await getCurrentUser();
      console.log('🔐 AuthService: Current user retrieved:', currentUser);
      
      const session = await fetchAuthSession();
      console.log('🔐 AuthService: Auth session retrieved:', {
        hasTokens: !!session.tokens,
        hasIdToken: !!session.tokens?.idToken,
        tokenType: session.tokens?.idToken ? typeof session.tokens.idToken : 'undefined'
      });
      
      if (!session.tokens?.idToken) {
        console.error('🔐 AuthService: No ID token in session!');
        return null;
      }
      
      const attributes = session.tokens.idToken.payload;
      if (process.env.NODE_ENV !== 'production') {
        console.log('🔐 AuthService: AWS Cognito attributes:', attributes);
      }
      
      // Normalize profile attributes
      const birthDate = attributes.birthdate || attributes['custom:birth_date'] || '';
      // Prefer custom age if present; otherwise compute from birthDate
      const computedAge = birthDate ? verifyAge(birthDate) : undefined;
      const age = attributes['custom:age'] ? parseInt(attributes['custom:age']) : computedAge;
      // Member since: prefer custom join date if present; otherwise fall back to auth_time (first auth) or iat
      let joinDate = attributes['custom:join_date'] || '';
      if (!joinDate) {
        const ts = attributes.auth_time || attributes.iat; // seconds since epoch
        if (ts) {
          const d = new Date(Number(ts) * 1000);
          if (!Number.isNaN(d.getTime())) joinDate = d.toISOString().split('T')[0];
        }
      }

      const userDetails = {
        id: currentUser.userId,
        email: attributes.email,
        name: attributes.name,
        phone: attributes.phone_number,
        birthDate,
        age,
        joinDate,
        isSubscriber: attributes['custom:is_subscriber'] === 'true',
        isAdmin: attributes['name'] === 'admin',
        entries: parseInt(attributes['custom:entries'] || '0'),
        purchases: JSON.parse(attributes['custom:purchases'] || '[]')
      };
      
      // AI CONTEXT: business-logic - Fetch subscription status from DynamoDB API
      // Reasoning: Subscription status is stored in DynamoDB, not Cognito custom attributes
      // Constraints: Must handle API failures gracefully and default to Cognito attribute as fallback
      // Future: Consider caching subscription status to reduce API calls
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        if (apiBaseUrl && session.tokens?.accessToken) {
          const subscriptionResponse = await fetch(`${apiBaseUrl}/my/subscription`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.tokens.accessToken.toString()}`
            }
          });
          
          if (subscriptionResponse.ok) {
            const subscriptionData = await subscriptionResponse.json();
            // If we got subscription data and it has a status, update isSubscriber
            if (subscriptionData && subscriptionData.status === 'active') {
              userDetails.isSubscriber = true;
              if (process.env.NODE_ENV === 'development') {
                console.log('🔐 AuthService: Subscription status loaded from API:', subscriptionData);
              }
            }
          }
        }
      } catch (subscriptionError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('🔐 AuthService: Failed to fetch subscription status, using Cognito attribute:', subscriptionError);
        }
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('User details with admin check:', userDetails);
      }
      return userDetails;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  // AI CONTEXT: security - Password reset functionality
  // Reasoning: Users need ability to reset forgotten passwords
  // Constraints: Must follow AWS Cognito password reset flow
  // Future: Consider implementing passwordless authentication

  async resetPassword(email) {
    try {
      if (!awsConfig.isConfigured) {
        toast.success('Password reset code sent! (Mock mode)');
        return { success: true };
      }

      await resetPassword({ username: email });
      toast.success('Password reset code sent to your email.');
      return { success: true };
    } catch (error) {
      return handleAuthError(error);
    }
  }

  // AI CONTEXT: security - Confirm password reset
  // Reasoning: Complete password reset process securely
  // Constraints: Must verify reset code and new password
  // Future: Consider implementing password strength requirements

  async confirmResetPassword(email, code, newPassword) {
    try {
      if (!awsConfig.isConfigured) {
        toast.success('Password reset successfully! (Mock mode)');
        return { success: true };
      }

      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword
      });
      
      toast.success('Password reset successfully! You can now sign in.');
      return { success: true };
    } catch (error) {
      return handleAuthError(error);
    }
  }

  // AI CONTEXT: security - Check authentication status
  // Reasoning: Determine if user is currently authenticated
  // Constraints: Must handle various authentication states
  // Future: Consider implementing token refresh logic

  async checkAuthStatus() {
    try {
      if (!awsConfig.isConfigured) {
        const storedUser = localStorage.getItem('tolbert_user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          return { isAuthenticated: true, user };
        }
        return { isAuthenticated: false, user: null };
      }

      const session = await fetchAuthSession();
      if (session.tokens) {
        const user = await this.getCurrentUserDetails();
        return { isAuthenticated: true, user };
      }
      return { isAuthenticated: false, user: null };
    } catch (error) {
      console.error('Auth status check error:', error);
      return { isAuthenticated: false, user: null };
    }
  }

  // AI CONTEXT: security - Get all users (admin only)
  // Reasoning: Admin needs to view all users for management
  // Constraints: Must be admin-only function
  // Future: Consider implementing pagination for large user bases

  async getAllUsers() {
    try {
      if (!awsConfig.isConfigured) {
        // Return mock users for development
        return mockUsers;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Getting all users from AWS Cognito via API...');
      }
      
      // Use the new API endpoint to get all users from Cognito
      if (isApiConfigured()) {
        try {
          // Use pagination to get all users
          let allUsers = [];
          let offset = 0;
          const limit = 25;  // Use 25 users per page to prevent 503 timeouts
          let hasMore = true;
          
          while (hasMore) {
            const response = await authorizedFetch(`/admin/users?limit=${limit}&offset=${offset}`, { method: 'GET' });
            
            if (response && Array.isArray(response.users)) {
              allUsers = allUsers.concat(response.users);
              
              // Check pagination info
              if (response.pagination) {
                hasMore = response.pagination.hasMore;
                offset += limit;
              } else {
                // Fallback for old API response format
                hasMore = response.users.length === limit;
                offset += limit;
              }
              
              if (process.env.NODE_ENV === 'development') {
                console.log(`Fetched ${response.users.length} users, total so far: ${allUsers.length}`);
              }
            } else {
              console.warn('Invalid response from users API:', response);
              throw new Error('Invalid response from users API');
            }
          }
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Successfully fetched all users from API:', allUsers.length);
          }
          return allUsers;
        } catch (apiError) {
          console.error('API call failed, falling back to known users:', apiError);
          // Fall through to known users fallback
        }
      }
      
      // Fallback to known users if API is not configured or fails
      if (process.env.NODE_ENV === 'development') {
        console.log('Using fallback known users list...');
      }
      const knownUsers = [
        {
          id: '81db4550-6071-70ec-4480-1888c5fd5c6b',
          email: 'admin@tolbert.com',
          name: 'admin',
          phone: '',
          birthDate: '',
          age: 0,
          joinDate: '2025-08-07',
          isSubscriber: false,
          isAdmin: true,
          entries: 0,
          purchases: [],
          status: 'CONFIRMED',
          enabled: true,
          createDate: '2025-08-07T20:49:34.756000-05:00',
          source: 'aws-cognito-fallback'
        },
        {
          id: '210b6570-b091-7009-5636-45f4521e9993',
          email: 'sbhooley@gmail.com',
          name: 'Steven Hooley2',
          phone: '',
          birthDate: '',
          age: 0,
          joinDate: '2025-08-07',
          isSubscriber: false,
          isAdmin: false,
          entries: 0,
          purchases: [],
          status: 'CONFIRMED',
          enabled: true,
          createDate: '2025-08-07T16:44:07.075000-05:00',
          source: 'aws-cognito-fallback'
        },
        {
          id: 'a18b45c0-3061-7089-11a1-ca2277206d67',
          email: 'robbietolbert3@gmail.com',
          name: 'Robbie Tolbert',
          phone: '',
          birthDate: '',
          age: 0,
          joinDate: '2025-08-08',
          isSubscriber: false,
          isAdmin: false,
          entries: 0,
          purchases: [],
          status: 'CONFIRMED',
          enabled: true,
          createDate: '2025-08-08T23:16:01.270000-05:00',
          source: 'aws-cognito-fallback'
        },
        {
          id: 'e14ba5b0-a0b1-707b-2963-de30a84edb19',
          email: 'steven@hooley.me',
          name: 'Steven Hooley',
          phone: '',
          birthDate: '',
          age: 0,
          joinDate: '2025-08-07',
          isSubscriber: false,
          isAdmin: false,
          entries: 0,
          purchases: [],
          status: 'CONFIRMED',
          enabled: true,
          createDate: '2025-08-07T16:39:19.810000-05:00',
          source: 'aws-cognito-fallback'
        }
      ];

      if (process.env.NODE_ENV === 'development') {
        console.log('Returning fallback users:', knownUsers);
      }
      return knownUsers;
    } catch (error) {
      console.error('Error getting all users:', error);
      
      // Final fallback to mock data if there's an error
      if (process.env.NODE_ENV === 'development') {
        console.log('Falling back to mock data due to error');
      }
      return mockUsers.map(user => ({
        ...user,
        source: 'aws-cognito-error-fallback'
      }));
    }
  }

  // AI CONTEXT: security - Update user attributes
  // Reasoning: Allow users to update their profile information
  // Constraints: Must handle AWS Cognito attribute updates
  // Future: Consider implementing profile picture upload

  async updateUserAttributes(attributes) {
    try {
      if (!awsConfig.isConfigured) {
        console.log('Update user attributes (Mock mode):', attributes);
        return { success: true };
      }

      console.log('Update user attributes:', attributes);
      return { success: true };
    } catch (error) {
      return handleAuthError(error);
    }
  }

  // AI CONTEXT: admin-security - Delete users from Cognito and DynamoDB
  // Reasoning: Allow admins to remove users and their associated data
  // Constraints: Must be admin-only, must clean up all user data
  // Future: Add soft delete option for data recovery

  async deleteUser(email) {
    try {
      if (!awsConfig.isConfigured) {
        console.log('Delete user (Mock mode):', email);
        return { success: true, message: 'User deleted (mock mode)' };
      }

      // Call the backend API to delete user from both Cognito and DynamoDB
      const response = await authorizedFetch('/admin/users', {
        method: 'DELETE',
        body: JSON.stringify({ email })
      });

      if (response && response.success) {
        toast.success(`User ${email} deleted successfully`);
        return { success: true, message: response.message };
      } else {
        throw new Error(response?.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(`Failed to delete user: ${error.message}`);
      return { success: false, error: error.message };
    }
  }

  // AI CONTEXT: admin-security - Delete multiple users
  // Reasoning: Allow bulk user deletion for efficiency
  // Constraints: Must be admin-only, must handle partial failures
  // Future: Add batch operation progress tracking

  async deleteMultipleUsers(emails) {
    try {
      if (!awsConfig.isConfigured) {
        console.log('Delete multiple users (Mock mode):', emails);
        return { success: true, message: `${emails.length} users deleted (mock mode)` };
      }

      // Call the backend API to delete multiple users
      const response = await authorizedFetch('/admin/users/bulk-delete', {
        method: 'DELETE',
        body: JSON.stringify({ emails })
      });

      if (response && response.success) {
        const deletedCount = response.deletedCount || emails.length;
        const failedCount = emails.length - deletedCount;
        
        if (failedCount > 0) {
          toast.success(`${deletedCount} users deleted, ${failedCount} failed`);
        } else {
          toast.success(`${deletedCount} users deleted successfully`);
        }
        
        return { 
          success: true, 
          deletedCount,
          failedCount,
          message: response.message 
        };
      } else {
        throw new Error(response?.message || 'Failed to delete users');
      }
    } catch (error) {
      console.error('Error deleting multiple users:', error);
      toast.error(`Failed to delete users: ${error.message}`);
      return { success: false, error: error.message };
    }
  }
}

// AI CONTEXT: security - Export singleton instance
// Reasoning: Ensure consistent authentication service across app
// Constraints: Must be easily importable and testable
// Future: Consider implementing service worker for offline auth

const authService = new AuthService();
export default authService; 